import { Button, Flex, Icon, Spinner, Text } from "@chakra-ui/react";
import { t } from "i18next";
import { useMemo } from "react";
import { FaEllipsisH } from "react-icons/fa";

export const Paging = ({
  limit,
  skip,
  setSkip,
  isLoading,
  count,
  hasNextPage,
}: {
  limit: number;
  skip: number;
  setSkip: (skip: number) => void;
  isLoading: boolean;
  count?: number;
  hasNextPage?: boolean;
}) => {
  const previousPage = useMemo(() => {
    return limit ? skip / limit : 1;
  }, [limit, skip]);

  const currentPage = useMemo(() => {
    return limit ? skip / limit + 1 : 1;
  }, [limit, skip]);

  const nextPage = useMemo(() => {
    return limit ? skip / limit + 2 : 1;
  }, [limit, skip]);

  const lastPage = useMemo(() => {
    return typeof count === "number" ? Math.max(1, Math.ceil(count / limit)) : undefined;
  }, [count, limit]);

  const isLastPage = typeof count === "number" ? count <= skip + limit : hasNextPage === false;

  const rangeLabel = useMemo(() => {
    if (typeof count !== "number") return undefined;
    if (count === 0) return `${0} ${t("out of")} ${0}`;
    const start = skip + 1;
    const end = Math.min(skip + limit, count);
    return `${start}-${end} ${t("out of")} ${count}`;
  }, [count, limit, skip]);

  return (
    <>
      <Flex alignItems={"center"} justifyContent={"center"} p="4" position="relative">
        <Flex alignItems={"center"} justifyContent={"center"} columnGap={"1"}>
          <Button
            colorScheme="teal"
            variant={"ghost"}
            size={"xs"}
            isDisabled={skip - limit < 0}
            onClick={() => setSkip(skip - limit)}
          >
            {t("Previous")}
          </Button>
          {count && previousPage > 1 ? (
            <>
              <Flex
                borderColor="smPrimary"
                border="1px solid"
                color="smPrimary"
                fontWeight={"bold"}
                fontSize={"xs"}
                rounded="lg"
                minWidth="6"
                height="6"
                justifyContent={"center"}
                alignItems={"center"}
                cursor="pointer"
                px="1"
                onClick={() => setSkip(0)}
              >
                {1}
              </Flex>
              <Icon as={FaEllipsisH} color="smPrimary" />
            </>
          ) : null}
          {skip / limit > 0 ? (
            <Flex
              borderColor="smPrimary"
              border="1px solid"
              color="smPrimary"
              fontWeight={"bold"}
              fontSize={"xs"}
              rounded="lg"
              minWidth="6"
              height="6"
              justifyContent={"center"}
              alignItems={"center"}
              cursor="pointer"
              onClick={() => setSkip(skip - limit)}
            >
              {previousPage}
            </Flex>
          ) : null}
          <Flex
            backgroundColor="smPrimary"
            color="smBackground"
            fontWeight={"bold"}
            fontSize={"xs"}
            rounded="lg"
            minWidth="6"
            height="6"
            justifyContent={"center"}
            alignItems={"center"}
          >
            {currentPage}
          </Flex>
          {!isLastPage && (!lastPage || nextPage <= lastPage) ? (
            <Flex
              borderColor="smPrimary"
              border="1px solid"
              color="smPrimary"
              fontWeight={"bold"}
              fontSize={"xs"}
              rounded="lg"
              minWidth="6"
              height="6"
              justifyContent={"center"}
              alignItems={"center"}
              cursor="pointer"
              onClick={() => setSkip(skip + limit)}
            >
              {nextPage}
            </Flex>
          ) : null}
          {count && lastPage && lastPage > nextPage ? (
            <>
              <Icon as={FaEllipsisH} color="smPrimary" />
              <Flex
                borderColor="smPrimary"
                border="1px solid"
                color="smPrimary"
                fontWeight={"bold"}
                fontSize={"xs"}
                rounded="lg"
                minWidth="6"
                height="6"
                justifyContent={"center"}
                alignItems={"center"}
                cursor="pointer"
                px="1"
                onClick={() => setSkip(count - (count % limit === 0 ? limit : count % limit))}
              >
                {lastPage}
              </Flex>
            </>
          ) : null}
          <Button
            colorScheme="teal"
            variant={"ghost"}
            size={"xs"}
            isDisabled={isLastPage}
            onClick={() => setSkip(skip + limit)}
          >
            {t("Next")}
          </Button>
        </Flex>
        <Flex position="absolute" right="4" minW="10" justifyContent={"flex-end"}>
          {isLoading && <Spinner color="smPrimary" size="sm" />}
        </Flex>
      </Flex>
      {rangeLabel && (
        <Flex alignItems={"center"} justifyContent={"center"} pb="4">
          <Text fontSize="xs" color="smMuted">
            {rangeLabel}
          </Text>
        </Flex>
      )}
    </>
  );
};
